/**
 * Record an interaction: type, when, what happened, files, outcome.
 *
 * Two pieces are worth reading closely.
 *
 * **Dictation.** The mic writes into the description and nowhere else. Final
 * utterances are appended to whatever is already typed, so speaking and typing
 * mix freely; interim results render underneath as a live preview rather than
 * in the textarea, because rewriting the field on every syllable would fight
 * anyone editing while they speak. Audio never leaves the browser — see
 * lib/voice.jsx.
 *
 * **Attachments upload before the interaction exists.** They cannot: an
 * attachment needs an owner id. So the interaction is created FIRST, and files
 * upload against it afterwards. That ordering is why the dialog reports
 * per-file progress and lets a failed file be retried without losing the text
 * — the interaction is already safe by then.
 */
import { useMutation, useQuery } from '@apollo/client';
import { Button, Spinner } from '@meshhub/design-tokens/ui';
import { Paperclip, RotateCw, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import {
  INTERACTION_STATUS_LABELS,
  formatBytes,
  isoDateAndTimeToInstant,
  isoToDateAndTime,
} from '../../lib/format.js';
import { uploadFile } from '../../lib/files.js';
import { ADMIN_LOOKUPS_QUERY, CREATE_LOOKUP, RECORD_INTERACTION } from '../../lib/queries.js';
import { S } from '../../lib/strings.js';
import { useSession } from '../../lib/session.jsx';
import { useDictation, VoiceButton } from '../../lib/voice.jsx';
import {
  FormDialog,
  JalaliDateField,
  MutationError,
  SelectField,
  TextArea,
  TextField,
} from '../FormKit.web.jsx';
import { VocabularyField } from '../VocabularyField.web.jsx';

const INTERACTION_STATUSES = ['DONE', 'PLANNED', 'CANCELLED'];

/** One queued file and where it got to. */
const queued = (file) => ({
  key: `${file.name}-${file.size}-${file.lastModified}`,
  file,
  progress: 0,
  state: 'queued', // queued | uploading | done | failed
  error: null,
});

/** A blank composer, dated now — the overwhelmingly common case. */
const blankForm = () => {
  const now = isoToDateAndTime(new Date().toISOString());
  return {
    typeId: '',
    date: now.date, // ISO; JalaliDateField renders and parses the Shamsi form
    time: now.time,
    description: '',
    outcome: '',
    status: 'DONE',
    followUp: '',
  };
};

export function InteractionComposer({ open, onOpenChange, contactId, onRecorded }) {
  const [form, setForm] = useState(blankForm);
  const [files, setFiles] = useState([]);
  const [interim, setInterim] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  const { isAdmin } = useSession();
  const { data: typeData, loading: typesLoading } = useQuery(ADMIN_LOOKUPS_QUERY, {
    variables: { kind: 'INTERACTION_TYPE', includeInactive: false },
    skip: !open,
  });
  const [recordInteraction] = useMutation(RECORD_INTERACTION);
  // «نوع تعامل» is required, so an unseeded INTERACTION_TYPE vocabulary is the
  // same dead end the calendar had: the composer opens and cannot be saved.
  const [createType, createTypeState] = useMutation(CREATE_LOOKUP, {
    refetchQueries: ['AdminLookups'],
    awaitRefetchQueries: true,
  });

  const types = typeData?.lookups ?? [];
  const set = (patch) => setForm((current) => ({ ...current, ...patch }));

  const dictation = useDictation({
    onTranscript: (text) =>
      setForm((current) => ({
        ...current,
        description: current.description ? `${current.description} ${text}` : text,
      })),
    onInterim: setInterim,
  });

  useEffect(() => {
    if (open) {
      setForm(blankForm());
      setFiles([]);
      setError(null);
      setInterim('');
    }
  }, [open]);

  const addFiles = (list) => {
    // THE FILELIST IS MATERIALISED HERE, before `setFiles` is called.
    //
    // `list` is the input element's LIVE `FileList`, and the change handler
    // resets `input.value` immediately afterwards so that choosing the same
    // file twice fires another change event. That reset EMPTIES the FileList.
    // React runs a functional updater during the render that follows the
    // handler, by which time the spread inside it saw nothing — so a file
    // picked with the button was dropped between being chosen and being
    // listed, and no error appeared anywhere.
    //
    // Dropping a file worked, which is why this survived: `dataTransfer.files`
    // is not the input's list and nothing resets it. So the feature was broken
    // exactly on the path most people use.
    const chosen = [...list].map(queued);
    setFiles((current) => {
      const known = new Set(current.map((f) => f.key));
      return [...current, ...chosen.filter((f) => !known.has(f.key))];
    });
  };

  const patchFile = (key, patch) =>
    setFiles((current) => current.map((f) => (f.key === key ? { ...f, ...patch } : f)));

  async function uploadOne(entry, interactionId) {
    patchFile(entry.key, { state: 'uploading', progress: 0, error: null });
    try {
      await uploadFile({
        file: entry.file,
        ownerType: 'INTERACTION',
        ownerId: interactionId,
        onProgress: (fraction) => patchFile(entry.key, { progress: fraction }),
      });
      patchFile(entry.key, { state: 'done', progress: 1 });
      return true;
    } catch (err) {
      patchFile(entry.key, { state: 'failed', error: err.message });
      return false;
    }
  }

  async function submit() {
    if (!form.typeId) {
      setError(new Error(S.interaction.typeRequired));
      return;
    }
    const occurredAt = isoDateAndTimeToInstant(form.date, form.time);
    if (!occurredAt) {
      setError(new Error(S.interaction.invalidDate));
      return;
    }

    setError(null);
    setBusy(true);
    if (dictation.listening) dictation.stop();

    try {
      // The follow-up note rides along at the end of the description until the
      // scheduling service gives it a home (Step 4; see todo.md). Labelled in
      // the text so it is obviously a note and not part of the account.
      const followUp = form.followUp.trim();
      const description = followUp
        ? `${form.description.trim()}\n\n— ${S.interaction.followUpNote}: ${followUp}`
        : form.description.trim();

      const { data } = await recordInteraction({
        variables: {
          input: {
            contactId,
            typeId: form.typeId,
            occurredAt,
            description,
            outcome: form.outcome.trim() || null,
            status: form.status,
          },
        },
      });

      const interactionId = data.recordInteraction.id;
      const pending = files.filter((f) => f.state !== 'done');
      const results = [];
      for (const entry of pending) {
        // Sequential rather than parallel: a congress-hall connection does
        // better with one upload at a time, and the progress bars stay legible.
        results.push(await uploadOne(entry, interactionId));
      }

      await onRecorded?.();

      if (results.every(Boolean)) {
        onOpenChange(false);
      } else {
        // The interaction saved; only some files did not. Keeping the dialog
        // open lets those be retried without retyping anything.
        setError(new Error(S.interaction.uploadFailed));
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={S.interaction.title}
      onSubmit={submit}
      submitLabel={S.app.save}
      busy={busy}
      wide
    >
      <MutationError error={error} />

      <div className="grid grid-cols-3 gap-4">
        <VocabularyField
          label={S.interaction.type}
          value={form.typeId}
          onChange={(id) => set({ typeId: id })}
          options={types}
          loading={typesLoading}
          includeNone
          required
          canCreate={isAdmin}
          codePrefix="INTERACTION"
          creating={createTypeState.loading}
          createError={createTypeState.error}
          onCreate={async ({ code, labelFa }) => {
            const created = await createType({
              variables: { input: { kind: 'INTERACTION_TYPE', code, labelFa } },
            });
            return created.data?.createLookup;
          }}
        />

        <JalaliDateField
          label={S.interaction.date}
          value={form.date}
          onChange={(iso) => set({ date: iso })}
        />

        <TextField
          label={S.interaction.time}
          value={form.time}
          onChange={(e) => set({ time: e.target.value })}
          dir="ltr"
          inputMode="numeric"
          placeholder="14:30"
        />
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-end gap-3">
          <TextArea
            label={S.interaction.description}
            className="flex-1"
            rows={5}
            value={form.description}
            onChange={(e) => set({ description: e.target.value })}
          />
        </div>
        <div className="flex items-center gap-3">
          <VoiceButton
            listening={dictation.listening}
            onToggle={dictation.toggle}
            disabled={busy}
          />
          {dictation.error && (
            <span role="alert" className="text-xs text-danger">
              {dictation.error}
            </span>
          )}
          {interim && <span className="text-sm text-text-muted">{interim}…</span>}
        </div>
      </div>

      <Dropzone
        files={files}
        onAdd={addFiles}
        onRemove={(key) => setFiles((c) => c.filter((f) => f.key !== key))}
        onRetry={(entry) => patchFile(entry.key, { state: 'queued', error: null })}
        inputRef={fileInputRef}
        disabled={busy}
      />

      <div className="grid grid-cols-2 gap-4">
        <TextField
          label={S.interaction.outcome}
          value={form.outcome}
          onChange={(e) => set({ outcome: e.target.value })}
        />
        <SelectField
          label={S.interaction.status}
          value={form.status}
          onChange={(e) => set({ status: e.target.value })}
        >
          {INTERACTION_STATUSES.map((status) => (
            <option key={status} value={status}>
              {INTERACTION_STATUS_LABELS[status]}
            </option>
          ))}
        </SelectField>
      </div>

      <TextField
        label={S.interaction.followUpNote}
        hint={S.interaction.followUpHint}
        value={form.followUp}
        onChange={(e) => set({ followUp: e.target.value })}
      />
    </FormDialog>
  );
}

function Dropzone({ files, onAdd, onRemove, onRetry, inputRef, disabled }) {
  const [over, setOver] = useState(false);

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm text-text-muted">{S.interaction.attachments}</span>

      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          onAdd(e.dataTransfer.files);
        }}
        className={`flex h-toolbar items-center justify-center gap-2 rounded border border-dashed
                    text-sm transition-colors disabled:opacity-50 ${
                      over
                        ? 'border-primary bg-surface-hover text-primary'
                        : 'border-border text-text-muted hover:bg-surface-hover'
                    }`}
      >
        <Paperclip size={14} aria-hidden="true" />
        {S.interaction.dropzone}
      </button>

      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(event) => {
          onAdd(event.target.files);
          event.target.value = '';
        }}
      />

      {files.length > 0 && (
        <ul className="flex flex-col gap-2">
          {files.map((entry) => (
            <li
              key={entry.key}
              className="flex items-center gap-3 rounded border border-border px-4 py-2 text-sm"
            >
              <span className="min-w-0 flex-1 truncate" title={entry.file.name}>
                {entry.file.name}
              </span>
              <span className="text-text-muted">{formatBytes(entry.file.size)}</span>

              {entry.state === 'uploading' && (
                <span className="flex items-center gap-2 text-text-muted">
                  <Spinner />
                  {S.interaction.uploading}
                </span>
              )}
              {entry.state === 'done' && <span className="text-success">{S.app.saved}</span>}
              {entry.state === 'failed' && (
                <>
                  <span className="text-danger" title={entry.error}>
                    {S.interaction.uploadFailed}
                  </span>
                  <Button variant="ghost" onClick={() => onRetry(entry)} title={S.app.retry}>
                    <RotateCw size={14} aria-hidden="true" />
                  </Button>
                </>
              )}
              {/* Removable only before it is committed — a completed upload is
                  already an attachment on a saved interaction. */}
              {entry.state !== 'done' && entry.state !== 'uploading' && (
                <Button
                  variant="ghost"
                  onClick={() => onRemove(entry.key)}
                  aria-label={S.app.remove}
                  title={S.app.remove}
                >
                  <X size={14} aria-hidden="true" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
